import './styles.css';
import { CountdownEvent, ViewMode } from './core/types';
import { loadEvents, saveEvents, addEvent, updateEvent, deleteEvent, generateId } from './core/store';
import { calcTimeDiff, getEffectiveTarget } from './core/time';
import { renderDashboard, renderArchive, renderSkeleton, destroyScroller } from './ui/dashboard';
import { showToast } from './ui/toast';
import { initTheme, createThemeToggle } from './ui/theme';
import { haptic, hapticHeavy, hapticSuccess, initPullToRefresh, initSwipeNav, getNextView } from './ui/mobile';

const app = document.getElementById('app')!;
const liveRegion = document.getElementById('live-region')!;

// Lazy-loaded modules — confetti + form + share are loaded on first use
let confettiInstance: { fire: () => void; stop: () => void } | null = null;

async function getConfetti(): Promise<{ fire: () => void; stop: () => void }> {
  if (confettiInstance) return confettiInstance;
  const { createConfetti } = await import('./utils/confetti');
  const canvas = document.getElementById('confetti-canvas') as HTMLCanvasElement;
  confettiInstance = createConfetti(canvas);
  return confettiInstance;
}

async function loadForm(): Promise<typeof import('./ui/form')> {
  return import('./ui/form');
}

async function loadShare(): Promise<typeof import('./utils/share')> {
  return import('./utils/share');
}

const NAV_ICONS = {
  dashboard:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>',
  archive:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>',
  compact:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><circle cx="4" cy="6" r="1"/><circle cx="4" cy="12" r="1"/><circle cx="4" cy="18" r="1"/></svg>',
  plus:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>',
};

let currentView: ViewMode = 'dashboard';
let compactMode = false;
let editingId: string | null = null;
let tickInterval: number | null = null;
const firedConfetti = new Set<string>();
let initialLoaded = false;
let cleanupMobileGestures: (() => void) | null = null;

function init(): void {
  initTheme();
  renderSkeletonFirst();
  window.setTimeout(async () => {
    await checkShareUrl();
    initialLoaded = true;
    render();
    startTick();
    setupNotifications();
  }, 320);
}

function renderSkeletonFirst(): void {
  app.innerHTML = '';
  app.appendChild(buildHeader());
  app.appendChild(renderSkeleton(3));
}

async function checkShareUrl(): Promise<void> {
  const { decodeShareUrl } = await loadShare();
  const shared = decodeShareUrl(window.location.href);
  if (!shared) return;

  const event: CountdownEvent = {
    id: generateId(),
    title: shared.title ?? 'Shared Event',
    targetDate: shared.targetDate ?? new Date().toISOString(),
    icon: shared.icon ?? '📅',
    color: shared.color ?? '#f59e0b',
    category: shared.category ?? 'holiday',
    repeatYearly: shared.repeatYearly ?? false,
    notifyIntervals: [60, 1440],
    createdAt: new Date().toISOString(),
  };
  addEvent(event);
  window.history.replaceState({}, '', window.location.pathname);
  window.setTimeout(() => {
    showToast(`Added "${event.title}" from a shared link.`, 'success', { title: 'Imported' });
  }, 500);
}

function startTick(): void {
  if (tickInterval) clearInterval(tickInterval);
  tickInterval = window.setInterval(() => {
    updateLiveCountdowns();
    checkCompletedEvents();
  }, 1000);
}

function updateLiveCountdowns(): void {
  const now = new Date();
  document.querySelectorAll('[data-event-id]').forEach(el => {
    const id = el.getAttribute('data-event-id')!;
    const events = loadEvents();
    const event = events.find(e => e.id === id);
    if (!event || event.completedAt) return;

    const target = getEffectiveTarget(event, now);
    const diff = calcTimeDiff(target, now);

    const digits = el.querySelectorAll('.digit');
    if (digits.length === 4) {
      const vals = [
        String(diff.days).padStart(2, '0'),
        String(diff.hours).padStart(2, '0'),
        String(diff.minutes).padStart(2, '0'),
        String(diff.seconds).padStart(2, '0'),
      ];
      digits.forEach((d, i) => {
        if (d.textContent !== vals[i]) {
          d.classList.add('flip-anim');
          d.textContent = vals[i];
          window.setTimeout(() => d.classList.remove('flip-anim'), 300);
        }
      });
    }
  });
}

function checkCompletedEvents(): void {
  const now = new Date();
  const events = loadEvents();
  let changed = false;

  for (const event of events) {
    if (event.completedAt) continue;
    const target = getEffectiveTarget(event, now);
    const diff = calcTimeDiff(target, now);

    if (diff.isPast && !firedConfetti.has(event.id)) {
      firedConfetti.add(event.id);
      getConfetti().then(c => c.fire());
      announceLive(`${event.title} has arrived.`);

      if (!event.repeatYearly) {
        event.completedAt = now.toISOString();
        changed = true;
      }
    }
  }

  if (changed) {
    saveEvents(events);
    render();
  }
}

function announceLive(message: string): void {
  liveRegion.textContent = message;
}

function buildHeader(): HTMLElement {
  const header = document.createElement('header');
  header.className = 'app-header';
  header.setAttribute('role', 'banner');

  const brand = document.createElement('a');
  brand.className = 'brand';
  brand.href = '#';
  brand.setAttribute('aria-label', 'Countdown — home');
  brand.innerHTML = `
    <svg class="brand-mark" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <circle cx="16" cy="16" r="14" stroke="currentColor" stroke-width="2" opacity="0.4"/>
      <circle cx="16" cy="16" r="9" stroke="currentColor" stroke-width="2"/>
      <line x1="16" y1="16" x2="16" y2="8" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
      <line x1="16" y1="16" x2="22" y2="16" stroke="currentColor" stroke-width="2" stroke-linecap="round" opacity="0.7"/>
      <circle cx="16" cy="16" r="2" fill="currentColor"/>
    </svg>
    <div>
      <span class="brand-wordmark">Countdown</span>
      <span class="brand-meta">Time, marked</span>
    </div>
  `;
  brand.addEventListener('click', (e) => {
    e.preventDefault();
    setView('dashboard');
  });

  const nav = document.createElement('nav');
  nav.className = 'app-nav';
  nav.setAttribute('role', 'navigation');
  nav.setAttribute('aria-label', 'Primary');

  const navButtons: Array<{ view: ViewMode | 'compact'; label: string; icon: string; primary?: boolean }> = [
    { view: 'dashboard', label: 'Live', icon: NAV_ICONS.dashboard },
    { view: 'archive', label: 'Archive', icon: NAV_ICONS.archive },
    { view: 'compact', label: 'Compact', icon: NAV_ICONS.compact },
    { view: 'create', label: 'New', icon: NAV_ICONS.plus, primary: true },
  ];

  for (const item of navButtons) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `nav-btn ${item.primary ? 'btn-create' : ''}`;
    btn.innerHTML = `${item.icon}<span>${item.label}</span>`;

    if (item.view === 'compact') {
      btn.setAttribute('aria-pressed', compactMode ? 'true' : 'false');
      btn.setAttribute('aria-label', `Toggle compact view${compactMode ? ', currently on' : ''}`);
      if (compactMode) btn.classList.add('active');
      btn.addEventListener('click', () => {
        compactMode = !compactMode;
        haptic();
        render();
      });
    } else {
      const isActive = currentView === item.view || (item.view === 'create' && (currentView === 'create' || currentView === 'edit'));
      if (isActive && !item.primary) btn.classList.add('active');
      btn.setAttribute('aria-label', item.label);
      btn.addEventListener('click', () => {
        haptic();
        setView(item.view as ViewMode);
      });
    }
    nav.appendChild(btn);
  }

  const themeToggle = createThemeToggle(() => {
    haptic();
    announceLive('Theme changed.');
  });

  header.appendChild(brand);
  const right = document.createElement('div');
  right.style.display = 'flex';
  right.style.alignItems = 'center';
  right.style.gap = '0.5rem';
  right.appendChild(themeToggle);
  header.appendChild(nav);
  header.appendChild(right);

  return header;
}

function setView(view: ViewMode): void {
  if (view === 'create') {
    editingId = null;
  }
  currentView = view;
  render();
}

function render(): void {
  if (!initialLoaded) {
    renderSkeletonFirst();
    return;
  }

  destroyScroller();
  app.innerHTML = '';
  app.appendChild(buildHeader());

  if (currentView === 'create' || currentView === 'edit') {
    const existing = editingId ? loadEvents().find(e => e.id === editingId) : undefined;
    loadForm().then(({ createForm }) => {
      const form = createForm(
        (event) => {
          if (editingId) {
            updateEvent(editingId, event);
            showToast('Countdown updated.', 'success');
          } else {
            addEvent(event);
            showToast(`Countdown for "${event.title}" started.`, 'success', { title: 'Created' });
          }
          hapticSuccess();
          editingId = null;
          currentView = 'dashboard';
          render();
        },
        () => {
          editingId = null;
          currentView = 'dashboard';
          render();
        },
        existing
      );
      app.appendChild(form);
    });
    return;
  }

  if (currentView === 'archive') {
    const archive = renderArchive(
      loadEvents(),
      (id) => {
        updateEvent(id, { completedAt: undefined });
        firedConfetti.delete(id);
        haptic();
        showToast('Restored to active countdowns.', 'info');
        render();
      },
      (id) => {
        if (confirm('Delete this archived event? This cannot be undone.')) {
          hapticHeavy();
          deleteEvent(id);
          showToast('Event deleted.', 'info');
          render();
        }
      },
      (id, memory) => {
        updateEvent(id, { memory });
        showToast('Memory saved.', 'success');
      }
    );
    app.appendChild(archive);
    setupMobileGestures();
    return;
  }

  const dashboard = renderDashboard(
    loadEvents(),
    compactMode,
    (id) => {
      editingId = id;
      currentView = 'edit';
      haptic();
      render();
    },
    (id) => {
      if (confirm('Delete this countdown?')) {
        hapticHeavy();
        deleteEvent(id);
        showToast('Countdown deleted.', 'info');
        render();
      }
    },
    (id) => {
      haptic();
      updateEvent(id, { completedAt: new Date().toISOString() });
      showToast('Moved to the archive.', 'success');
      render();
    },
    (id) => {
      const events = loadEvents();
      const event = events.find(e => e.id === id);
      if (!event) return;
      loadShare().then(({ encodeShareUrl }) => {
        const url = encodeShareUrl(event);
        navigator.clipboard.writeText(url).then(
          () => {
            hapticSuccess();
            showToast('Share link copied to clipboard.', 'success', { title: 'Copied' });
          },
          () => showToast('Could not copy link. Try again.', 'error')
        );
      });
    },
    () => {
      haptic();
      setView('create');
    }
  );
  app.appendChild(dashboard);
  setupMobileGestures();
}

function setupMobileGestures(): void {
  if (cleanupMobileGestures) {
    cleanupMobileGestures();
    cleanupMobileGestures = null;
  }

  if (!('ontouchstart' in window)) return;

  const viewContainer = app.querySelector('.view-container') as HTMLElement | null;
  if (!viewContainer) return;

  const cleanups: Array<() => void> = [];

  if (currentView === 'dashboard') {
    cleanups.push(
      initPullToRefresh({
        container: document.body,
        onRefresh: async () => {
          render();
        },
      })
    );
  }

  if (currentView === 'dashboard' || currentView === 'archive') {
    cleanups.push(
      initSwipeNav({
        container: viewContainer,
        onSwipeLeft: () => {
          const next = getNextView(currentView, 'left');
          if (next) setView(next);
        },
        onSwipeRight: () => {
          const next = getNextView(currentView, 'right');
          if (next) setView(next);
        },
      })
    );
  }

  cleanupMobileGestures = () => cleanups.forEach(fn => fn());
}

function setupNotifications(): void {
  if (!('Notification' in window)) return;
  if (Notification.permission === 'default') {
    Notification.requestPermission();
  }

  window.setInterval(() => {
    if (Notification.permission !== 'granted') return;
    const now = new Date();
    const events = loadEvents().filter(e => !e.completedAt);

    for (const event of events) {
      const target = new Date(getEffectiveTarget(event, now));
      const msUntil = target.getTime() - now.getTime();
      const minsUntil = msUntil / (1000 * 60);

      for (const interval of event.notifyIntervals) {
        if (Math.abs(minsUntil - interval) < 0.5) {
          new Notification(`${event.icon} ${event.title}`, {
            body: `${interval} minutes until event!`,
            icon: '/icon-192.png',
          });
        }
      }
    }
  }, 30000);
}

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/sw.js').catch(() => {});
}

init();
